import { UploadFileOutlined as UploadFileOutlinedIcon } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import { useTheme } from '@mui/material/styles';
import is from '@sindresorhus/is';
import { AnimatePresence, motion } from 'framer-motion';
import type { FunctionComponent } from 'react';
import { Fragment, useContext } from 'react';
import { useParams } from 'react-router';

import { ApplicationContext } from '~/shared/context/context';
import { useUserHardSoftSkillsOptionsQuery, useUserHardSoftSkillsQuery } from '~/shared/query-hooks/hard-soft-skills';

import { ExportButton } from '../../../../design/components/button/ExportButton';
import { SecondaryActionButton } from '../../../../design/components/button/SecondaryAction';
import { InlineLoadingSpinner } from '../../../../design/components/LoadingSpinner';
import { EditButton } from '../../../common/buttons/EditButton';
import { ComponentErrorBoundary } from '../../../error-handling/ErrorBoundaries';
import { useSnackbars } from '../../../error-handling/SnackBars';
import { useProfileExportMutation, useSkillsBannersQuery } from '../../../query-hooks/profile';
import { useUserSettingsMutation, useUserSettingsQuery } from '../../../query-hooks/user-settings';
import { useScrollToLocationHash } from '../../../use-scroll-to-location-hash';
import { route as cvReimportRoute } from '../../cv-reimport/constants';
import { AboutMe } from '../common-section/AboutMe';
import { Ambitions } from '../common-section/ambitions/Ambitions';
import { GeneralInformation } from '../common-section/GeneralInformation';
import { Languages } from '../common-section/languages/Language';
import { Navigation } from '../common-section/Navigation';
import { Skills } from '../common-section/Skills';
import { Sidebar } from '../common-section/sidebar/SideBar';
import { editUserAboutMeUrl, editUserSkillsUrl, route } from '../constants';
import { AddButton } from '../user-profile/common/ActionButtons';
import { UserHistory } from './WorkHistory';

const bannersVariants = {
    initial: { opacity: 0.2, y: '-100%' },
    show: { opacity: 1, transition: { delay: 0.3 }, y: '0px' },
};

export const userProfileDataCy = {
    proficiencyBanner: 'user-profile-proficiency-banner',
    skillsBanner: 'user-profile-skills-banner',
} as const;

const BannerMessage: FunctionComponent<{
    readonly onClose: () => void;
    readonly text: string;
}> = ({ onClose, text, ...props }) => {
    const theme = useTheme();
    const warningColor = theme.palette.warning.contrastText;

    return (
        <motion.div
            animate={bannersVariants.show}
            exit={bannersVariants.initial}
            initial={bannersVariants.initial}
            {...props}
            style={{
                position: 'absolute',
                width: '100%',
            }}
        >
            <Alert
                icon={false}
                severity="warning"
                sx={{
                    '& .MuiAlert-action': { color: warningColor },
                    '& .MuiAlert-message': {
                        color: warningColor,
                        flexGrow: 1,
                    },
                    fontSize: 14,
                    textAlign: 'center',
                    zIndex: 1,
                }}
                variant="filled"
                onClose={onClose}
            >
                {text}
            </Alert>
        </motion.div>
    );
};

export const SkillsBanners: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);
    const { data: skillsBanners } = useSkillsBannersQuery();
    const userSettingsMutation = useUserSettingsMutation();

    const { addSkillsBannerQuery } = useUserSettingsQuery('addSkillsBanner');
    const { addSkillsProficiencyBannerQuery } = useUserSettingsQuery('addSkillsProficiencyBanner');

    const { showErrorSnackbar } = useSnackbars();

    const hasSkillsBanner = skillsBanners?.addSkills === 'display';
    const hasProficiencyBanner = skillsBanners?.addSkillsProficiency === 'display';

    const isSkillsBannerSettingEnabled = addSkillsBannerQuery.data === 'enabled';
    const isProficiencyBannerSettingEnabled = addSkillsProficiencyBannerQuery.data === 'enabled';

    const isSkillsOpen = hasSkillsBanner && isSkillsBannerSettingEnabled;
    const isProficiencyOpen = hasProficiencyBanner && isProficiencyBannerSettingEnabled;

    const hasBanner = isSkillsOpen || isProficiencyOpen;

    const stackHeight = 40;
    const stackEnterDelay = 0.3;

    const handleSkillsClose = (): void =>
        userSettingsMutation.mutate({ addSkillsBanner: 'disabled' }, { onError: showErrorSnackbar });
    const handleProficiencyClose = (): void =>
        userSettingsMutation.mutate({ addSkillsProficiencyBanner: 'disabled' }, { onError: showErrorSnackbar });

    return (
        <motion.div
            animate={{
                height: hasBanner ? stackHeight : 0,
                transition: { delay: hasBanner ? stackEnterDelay : 0 },
            }}
            initial={{ height: 0 }}
        >
            <AnimatePresence>
                {isSkillsOpen && (
                    <BannerMessage
                        key="skills"
                        data-cy={userProfileDataCy.skillsBanner}
                        text={translate('pages.user-profile.banners.minimum-skills-requirement.message')}
                        onClose={handleSkillsClose}
                    />
                )}
                {!isSkillsOpen && isProficiencyOpen && (
                    <BannerMessage
                        key="proficiency"
                        data-cy={userProfileDataCy.proficiencyBanner}
                        text={translate('pages.user-profile.banners.max-unknown-proficiency-limit.message')}
                        onClose={handleProficiencyClose}
                    />
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export const HeaderBackground: FunctionComponent = () => {
    const { applicationState } = useContext(ApplicationContext);
    const profileBackgroundImageUrl = applicationState.customization
        .get('uiConfiguration')
        .get('assets')
        .get('profileBackgroundImageUrl');

    if (profileBackgroundImageUrl.isJust) {
        return (
            <Box
                sx={{
                    backgroundImage: `url(${profileBackgroundImageUrl.value.href})`,
                    backgroundPosition: 'top',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    borderRadius: '0.75rem',
                    height: 240,
                    position: 'relative',
                    width: '100%',
                    zIndex: -1,
                }}
            />
        );
    }

    return null;
};

const ProfileExportButton: FunctionComponent = () => {
    const { isPending, mutate } = useProfileExportMutation();
    const { applicationState, translate } = useContext(ApplicationContext);
    const { myUserProfile } = applicationState;
    const userId = useParams()[route.params.userId]!;
    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbars();

    const myUserProfileId = myUserProfile.get('id').unwrapOr('');

    if (userId !== myUserProfileId) {
        return null;
    }

    const downloadProfileExport = (): void =>
        mutate(undefined, {
            onError: showErrorSnackbar,
            onSuccess: ({ downloadLink }) => {
                showSuccessSnackbar('success.profile.exported');
                window.open(downloadLink, '_blank');
            },
        });

    return (
        <ExportButton
            isPending={isPending}
            label={translate(isPending ? 'common.exporting' : 'common.export')}
            onClick={downloadProfileExport}
        />
    );
};

const ProfileCvReimportButton: FunctionComponent = () => {
    const { translate } = useContext(ApplicationContext);

    return (
        <Link href={cvReimportRoute.patterns.cvReimport} underline="none">
            <SecondaryActionButton
                label={translate('pages.cv-reimport.headline')}
                startIcon={<UploadFileOutlinedIcon />}
            />
        </Link>
    );
};

const ProfileActionsButtons: FunctionComponent = () => (
    <Grid container={true} gap={2} justifyContent="center">
        <ProfileCvReimportButton />
        <ProfileExportButton />
    </Grid>
);

const SkillsLoader: FunctionComponent = () => {
    const { actions } = useContext(ApplicationContext);
    const userId = useParams()[route.params.userId]!;
    const { data } = useUserHardSoftSkillsOptionsQuery(userId);
    const { data: hardSoftSkillsData, isPending } = useUserHardSoftSkillsQuery({
        enabled: Boolean(data?.canReadSkills),
        userId,
    });

    if (isPending) {
        return <InlineLoadingSpinner />;
    }

    const UpdateButton = is.array(hardSoftSkillsData) && is.nonEmptyArray(hardSoftSkillsData) ? EditButton : AddButton;

    return (
        <Skills
            actions={
                <UpdateButton aria-label="Edit Skills" onClick={() => actions.navigateTo(editUserSkillsUrl(userId))} />
            }
            userId={userId}
        />
    );
};

export const UserProfile: FunctionComponent = () => {
    const { actions } = useContext(ApplicationContext);
    const userId = useParams()[route.params.userId]!;
    useScrollToLocationHash();

    return (
        <Fragment>
            <SkillsBanners />
            <HeaderBackground />
            <Grid
                columnGap={2}
                container={true}
                direction="row"
                flexWrap="nowrap"
                sx={{ maxWidth: 1400, mt: -12, mx: 'auto', pb: 2, px: 2 }}
            >
                <Grid sx={{ display: { xs: 'none', lg: 'block' }, flexShrink: 0 }}>
                    <Navigation />
                </Grid>
                <Grid container={true} flexDirection="column" gap={2} size="grow" sx={{ minWidth: 0 }}>
                    <GeneralInformation actionButtons={<ProfileActionsButtons />} userId={userId} />
                    <AboutMe
                        actions={
                            <EditButton
                                aria-label="Edit About Me"
                                onClick={() => actions.navigateTo(editUserAboutMeUrl(userId))}
                            />
                        }
                        userId={userId}
                    />
                    <ComponentErrorBoundary message="errors.component.profile.skills">
                        <SkillsLoader />
                    </ComponentErrorBoundary>
                    <ComponentErrorBoundary message="errors.component.profile.languages">
                        <Languages userId={userId} />
                    </ComponentErrorBoundary>
                    <ComponentErrorBoundary message="errors.component.profile.ambitions">
                        <Ambitions userId={userId} />
                    </ComponentErrorBoundary>
                    <UserHistory userId={userId} />
                </Grid>
                <Grid size={3} sx={{ minWidth: 240 }}>
                    <Sidebar userId={userId} />
                </Grid>
            </Grid>
        </Fragment>
    );
};
